import styled from "@emotion/styled";
import {
  ArrowDownward,
  ArrowUpward,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { Box, Link, Table as MuiTable, Stack, Typography } from "@mui/joy";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  Header as HeaderType,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import { ApiTargetWithCount } from "griptonite-api-client";
import { sumBy } from "lodash";
import { memo, useEffect, useMemo } from "react";

import { useVenueZonesByUuid } from "@/hooks/routes";
import { useTargets } from "@/hooks/targets";
import { useCurrentVenueUuid } from "@/hooks/venues";

// Mock data generator
const generateMockTargets = (): ApiTargetWithCount[] => {
  const grades = ["V0", "V1", "V2", "V3", "V4", "V5", "V6", "V7", "V8"];

  const mockTargets: ApiTargetWithCount[] = [];

  grades.forEach((grade, gradeIndex) => {
    const targetCount = Math.floor(Math.random() * 15) + 5;
    const currentCount = Math.floor(Math.random() * (targetCount + 5));

    mockTargets.push({
      uuid: `mock-target-${gradeIndex}`,
      zone: null,
      gradeValue: grade,
      gradeInternal: gradeIndex * 100,
      targetCount,
      currentCount,
    } as ApiTargetWithCount);
  });

  return mockTargets;
};

const useCurrentTargetUuid = () => {
  const params = useParams({ strict: false }) as { targetUuid?: string };
  return params.targetUuid;
};

const StyledTh = styled.th`
  &.active {
    background: #e5efff;
    position: relative;
    border: 1px solid #0084ff;
    border-bottom-width: 1px !important;
  }
`;

const columnHelper = createColumnHelper<ApiTargetWithCount>();

const TableHeader = ({ header }: { header: HeaderType<ApiTargetWithCount, unknown> }) => {
  const isSorted = header.column.getIsSorted();
  return (
    <StyledTh>
      <Link
        underline="none"
        textColor={header.column.getIsSorted() ? "primary.500" : "neutral.800"}
        onClick={header.column.getToggleSortingHandler()}
        startDecorator={isSorted ? isSorted === "desc" ? <ArrowDownward /> : <ArrowUpward /> : null}
        sx={{ flex: 1 }}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </Link>
    </StyledTh>
  );
};

const TableRow = ({ row }: { row: Row<ApiTargetWithCount> }) => {
  const currentTargetUuid = useCurrentTargetUuid();
  const isSelected = currentTargetUuid && currentTargetUuid === row.id;
  // if (isSelected) {
  //   debugger;
  // }
  const navigate = useNavigate();

  const openSidebar = () => {
    navigate({
      from: "/gym/$venueUuid/settings/targets/$climbType",
      to: "./$targetUuid",
      params: { targetUuid: row.original.uuid },
      replace: true,
    });
  };

  return (
    <tr
      role="link"
      onClick={row.getIsGrouped() ? row.getToggleExpandedHandler() : openSidebar}
      style={{
        cursor: "pointer",
        ...(isSelected
          ? {
              "--TableCell-dataBackground": "var(--TableCell-selectedBackground)",
              "--TableCell-headBackground": "var(--TableCell-selectedBackground)",
            }
          : {}),
        ...(row.getIsGrouped()
          ? {
              fontWeight: "bold",
              backgroundColor: "var(--joy-palette-neutral-50)",
            }
          : {}),
      }}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <td key={cell.id} className={cell.column.id}>
            {cell.getIsGrouped() ? (
              <Stack direction="row" alignItems="center" gap={0}>
                {row.getIsExpanded() ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Stack>
            ) : cell.getIsAggregated() ? (
              flexRender(
                cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                cell.getContext(),
              )
            ) : cell.getIsPlaceholder() ? null : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </td>
        );
      })}
    </tr>
  );
};

const ZoneCell = memo(
  ({ uuid, climbType }: { uuid: string | null; climbType: "BOULDER" | "SPORT" }) => {
    const venueUuid = useCurrentVenueUuid();

    const zonesByUuid = useVenueZonesByUuid(venueUuid, climbType);
    if (!uuid) return "(No zone)";
    const zone = zonesByUuid?.[uuid];
    return <>{zone ? zone.name : "–"}</>;
  },
);
ZoneCell.displayName = "ZoneCell";

const ColorNumber = ({
  children,
  color,
  ...props
}: {
  children: React.ReactNode;
  color?: "neutral" | "warning" | "danger";
} & React.ComponentProps<typeof Typography>) => {
  if (color === "neutral" || !color) {
    return <Typography>{children}</Typography>;
  }
  return (
    <Typography
      textColor={`${color}.600`}
      sx={{
        backgroundColor: `${color}.100`,
        display: "inline-block",
        px: 0.5,
        borderRadius: 1,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};

const getColorForDifference = (
  difference: number,
  config?: { tolerance: number; warningThreshold: number },
): "neutral" | "warning" | "danger" => {
  if (!config) return "neutral";

  const absDifference = Math.abs(difference);
  if (absDifference > config.tolerance) {
    return "warning";
  } else if (absDifference > config.warningThreshold) {
    return "danger";
  }
  return "neutral";
};

export default function TargetsTable({
  climbType,
  groupBy,
  showAllGrades,
  displayMockData,
}: {
  climbType: "BOULDER" | "SPORT";
  groupBy?: "zone" | "gradeValue" | "ascentStyle";
  showAllGrades?: boolean;
  displayMockData?: boolean;
}) {
  const venueUuid = useCurrentVenueUuid();
  const targetsQuery = useTargets(venueUuid, climbType);
  const realZonesByUuid = useVenueZonesByUuid(venueUuid, climbType);

  // Use mock data if mockData prop is true, otherwise use real data
  const targets = useMemo(() => {
    if (displayMockData) {
      return generateMockTargets();
    }
    return targetsQuery.data?.targets || [];
  }, [displayMockData, targetsQuery.data?.targets]);

  const mode = useMemo(() => {
    if (displayMockData) {
      return "GLOBAL"; // Simple global mode for mock data
    }
    return targetsQuery.data?.config.mode;
  }, [displayMockData, targetsQuery.data?.config.mode]);

  const zonesByUuid = realZonesByUuid;

  const columns = [
    ...(mode === "BY_ZONE"
      ? [
          columnHelper.accessor("zone", {
            header: "Zone",
            id: "zone",
            cell: (props) => <ZoneCell uuid={props.getValue()} climbType={climbType} />,
            sortingFn: (rowA, rowB) => {
              const zoneA = zonesByUuid?.[rowA.original.zone || ""]?.name || "";
              const zoneB = zonesByUuid?.[rowB.original.zone || ""]?.name || "";
              return zoneA.localeCompare(zoneB);
            },
          }),
        ]
      : []),
    ...(mode === "BY_ASCENT_STYLE"
      ? [
          columnHelper.accessor("ascentStyle", {
            header: "Ascent style",
            id: "ascentStyle",
            cell: (props) => {
              const style = props.getValue();
              if (style === "TOPROPE") return "Toprope";
              if (style === "LEAD") return "Lead";
              if (style === "AUTO_BELAY") return "Auto belay";
              return style;
            },
          }),
        ]
      : []),
    columnHelper.accessor("gradeValue", {
      header: "Grade",
      id: "gradeValue",
      cell: (props) => props.getValue() || "–",
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.gradeInternal ?? -Infinity;
        const b = rowB.original.gradeInternal ?? -Infinity;
        return a - b;
      },
    }),
    columnHelper.accessor("targetCount", {
      header: "Target number",
      id: "targetCount",
      aggregationFn: "sum",
      aggregatedCell: (props) => props.getValue(),
    }),
    columnHelper.accessor("currentCount", {
      header: "Current number",
      id: "currentCount",
      aggregationFn: "sum",
      aggregatedCell: (props) => {
        const rows = props.row.subRows;
        const totalCurrent = props.getValue();
        const totalTarget = sumBy(rows, (row) => row.original.targetCount);
        const difference = totalCurrent - totalTarget;
        const color = getColorForDifference(difference, targetsQuery.data?.config);

        return <ColorNumber color={color}>{totalCurrent}</ColorNumber>;
      },
      cell: (props) => {
        const current = props.getValue();
        if (typeof current !== "number") return "–";

        const target = props.row.original.targetCount;
        const difference = current - target;
        const color = getColorForDifference(difference, targetsQuery.data?.config);

        return <ColorNumber color={color}>{current}</ColorNumber>;
      },
    }),
    columnHelper.display({
      header: "Difference",
      id: "difference",
      cell: (props) => {
        const current = props.row.original.currentCount;
        const target = props.row.original.targetCount;

        if (typeof current !== "number") return "–";

        const difference = current - target;
        const sign = difference > 0 ? "+" : "";
        const color = getColorForDifference(difference, targetsQuery.data?.config);

        return <ColorNumber color={color}>{`${sign}${difference}`}</ColorNumber>;
      },
      aggregatedCell: (props) => {
        const rows = props.row.subRows;
        const totalCurrent = sumBy(rows, (row) => row.original.currentCount || 0);
        const totalTarget = sumBy(rows, (row) => row.original.targetCount);
        const difference = totalCurrent - totalTarget;
        const sign = difference > 0 ? "+" : "";
        const color = getColorForDifference(difference, targetsQuery.data?.config);

        return <ColorNumber color={color}>{`${sign}${difference}`}</ColorNumber>;
      },
    }),
  ];

  const targetsFiltered = useMemo(() => {
    if (showAllGrades) {
      return targets;
    }
    return targets.filter((t) => t.currentCount !== 0 && t.targetCount !== 0);
  }, [showAllGrades, targets]);

  const table = useReactTable<ApiTargetWithCount>({
    columns,
    data: targetsFiltered,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableSortingRemoval: false,
    getRowId: (original) => original.uuid,
    initialState: {
      sorting: [
        ...(mode === "BY_ZONE" || mode === "BY_ASCENT_STYLE"
          ? [{ id: "zone" as const, desc: false }]
          : []),
        ...(mode === "BY_ASCENT_STYLE" ? [{ id: "ascentStyle" as const, desc: false }] : []),
        { id: "gradeValue" as const, desc: false },
      ],
      grouping: groupBy ? [groupBy] : [],
      expanded: {},
    },
  });

  useEffect(() => {
    table.setSorting([
      ...(mode === "BY_ZONE" || mode === "BY_ASCENT_STYLE"
        ? [{ id: "zone" as const, desc: false }]
        : []),
      ...(mode === "BY_ASCENT_STYLE" ? [{ id: "ascentStyle" as const, desc: false }] : []),
      { id: "gradeValue" as const, desc: false },
    ]);
    table.setGrouping(groupBy ? [groupBy] : []);
  }, [table, mode, groupBy]);

  return (
    <Box sx={{ overflow: "auto", flex: 1 }}>
      <MuiTable
        borderAxis="bothBetween"
        hoverRow
        sx={{
          td: { position: "relative" },
          width: "auto",
          "th, td": {
            paddingX: 1,
            paddingY: 1,
            width: 120,
          },
          "th.zone, td.zone": {
            width: 200,
          },
          "td.gradeValue": {
            fontWeight: "bold",
            // textAlign: "right",
          },
          th: {
            backgroundColor: "neutral.100",
          },
          "--TableCell-selectedBackground": (theme) => theme.vars.palette.primary.softBg,
          "--TableRow-hoverBackground": (theme) => theme.vars.palette.rowHoverBackground,
        }}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return <TableHeader key={header.id} header={header} />;
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            return <TableRow key={row.id} row={row} />;
          })}
        </tbody>
      </MuiTable>
    </Box>
  );
}
